import { observer } from 'mobx-react-lite';
import { ContextualMenuItemType, IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import { getRTL } from '@fluentui/react/lib/Utilities';
import { assertNever } from 'owa-assert';
import { MailboxInfo } from 'owa-client-ids';
import { formatPastRelativeDateTime, formatUserDateTime, owaDate, OwaDate } from 'owa-datetime';
import { CloudIconWithOverlay, getIconCSSForAttachment } from 'owa-file-icon';
import { getGuid } from 'owa-guid';
import loc, { format } from 'owa-localize';
import { observableNow } from 'owa-observable-datetime';
import Item from 'owa-service/lib/contract/Item';
import ItemId from 'owa-service/lib/contract/ItemId';
import getAttachmentPolicy from 'owa-session-store/lib/utils/getAttachmentPolicy';
import * as trace from 'owa-trace';
import * as React from 'react';
import { getAttachmentSuggestionsAction } from '../actions/publicLazyActions';
import {
    ReplyWithAttachmentSuggestion,
    ReplyWithEndpointType,
} from '../store/schema/AttachmentSuggestions';
import AttachmentSuggestionsMenuItems from './AttachmentSuggestionsMenuItems';
import extractAttachmentSuggestions from './extractAttachmentSuggestions';
import {
    accessedOnText,
    replyWithAttachmentLastModifiedText,
    replyWithMenuHeaderText,
    suggestedFilesText,
} from './getAttachmentSuggestionsMenuItems.locstring.json';
import logAttachmentSuggestionClicked from './logAttachmentSuggestionClicked';
import {
    getAttachmentSuggestions,
    getAttachmentSuggestionsTraceId,
    logAttachmentMenuSuggestionData,
    logAttachMenuData,
    logAttachMenuDataOnClose,
} from './logAttachMenuData';
import { logUsage } from 'owa-analytics';

const TRUNCATION_SEPARATOR: string = '...';
const MAX_FILENAME_LENGTH: number = 35;

const MAX_BREADCRUMBS_LENGTH: number = 50;

const LTR_PATH_SEPARATOR = ' > ';
const RTL_PATH_SEPARATOR = ' < ';

const styles = require('./AttachmentSuggestionsMenuItem.scss');

export default function getAttachmentSuggestionsMenuItems(
    isMail: boolean
): AttachmentSuggestionsMenuItems {
    let logicalId = getGuid();
    let traceId: string = null;
    let attachMenuOpenTime: number = 0;
    const getMenuItems = (
        shouldUseSuggestions: boolean,
        referenceItemId: ItemId,
        referenceAttachmentsSupported: boolean,
        classicAttachmentsSupported: boolean
    ) => {
        let attachmentSuggestions = getAttachmentSuggestions(referenceItemId);

        if (shouldUseSuggestions && !!attachmentSuggestions?.length) {
            attachmentSuggestions = attachmentSuggestions.filter(suggestion =>
                filterSuggestions(
                    suggestion,
                    referenceAttachmentsSupported,
                    classicAttachmentsSupported
                )
            );

            return extractAttachmentSuggestions(attachmentSuggestions);
        }

        return [];
    };
    return {
        getContextualMenuItems: (
            shouldUseSuggestions: boolean,
            referenceItemId: ItemId,
            referenceAttachmentsSupported: boolean,
            classicAttachmentsSupported: boolean,
            shouldUseSuggetedFilesTitle: boolean,
            onAttachmentClicked: (
                suggestion: ReplyWithAttachmentSuggestion,
                suggestionIndex: number
            ) => void
        ) => {
            const suggestions = getMenuItems(
                shouldUseSuggestions,
                referenceItemId,
                referenceAttachmentsSupported,
                classicAttachmentsSupported
            );

            if (traceId) {
                logAttachmentMenuSuggestionData(
                    suggestions,
                    logicalId,
                    traceId,
                    () => {
                        traceId = null;
                    },
                    attachMenuOpenTime,
                    referenceItemId
                );
            }

            return buildMenuItems(suggestions, onAttachmentClicked, shouldUseSuggetedFilesTitle);
        },
        onMenuOpened: (
            shouldUseSuggestions: boolean,
            referenceItem: Item,
            mailboxInfo: MailboxInfo,
            composeId: string,
            defaultButtonsList: string,
            referenceAttachmentsSupported: boolean,
            classicAttachmentsSupported: boolean
        ) => {
            attachMenuOpenTime = new Date().getTime();
            if (shouldUseSuggestions) {
                getAttachmentSuggestionsAction(referenceItem, mailboxInfo, composeId);
            }

            const suggestions = getMenuItems(
                shouldUseSuggestions,
                referenceItem?.ItemId,
                referenceAttachmentsSupported,
                classicAttachmentsSupported
            );

            const currentLogicalId = logicalId;
            const newLogicalId = getGuid();
            logicalId = currentLogicalId;

            // Log info about the buttons shown in the menu.
            logAttachMenuData(
                suggestions,
                referenceItem?.ItemId,
                defaultButtonsList,
                currentLogicalId,
                newLogicalId,
                isMail
            );

            traceId = getAttachmentSuggestionsTraceId(referenceItem?.ItemId);
        },
        onMenuDismissed: (
            shouldUseSuggestions: boolean,
            referenceItemId: ItemId,
            referenceAttachmentsSupported: boolean,
            classicAttachmentsSupported: boolean
        ) => {
            const suggestions = getMenuItems(
                shouldUseSuggestions,
                referenceItemId,
                referenceAttachmentsSupported,
                classicAttachmentsSupported
            );

            // Log that the menu has been dismissed.
            logAttachMenuDataOnClose(suggestions, isMail);

            traceId = null;
        },
    };
}

function classicAttachmentsPolicyEnabled(): boolean {
    return getAttachmentPolicy().ClassicAttachmentsEnabled;
}

function referenceAttachmentsPolicyEnabled(): boolean {
    return getAttachmentPolicy().ReferenceAttachmentsEnabled;
}

function filterSuggestions(
    suggestion: ReplyWithAttachmentSuggestion,
    referenceAttachmentsSupported: boolean,
    classicAttachmentsSupported: boolean
): boolean {
    if (!suggestion) {
        return false;
    }
    switch (suggestion.Endpoint) {
        case ReplyWithEndpointType.Contextual:
        case ReplyWithEndpointType.MRUCloudy:
        case ReplyWithEndpointType.MRUContextual:
            return referenceAttachmentsPolicyEnabled() && referenceAttachmentsSupported;
        case ReplyWithEndpointType.MRUClassic:
        case ReplyWithEndpointType.MRUClassicContextual:
            return classicAttachmentsPolicyEnabled() && classicAttachmentsSupported;
        default:
            return assertNever(suggestion.Endpoint);
    }
}

function buildMenuItems(
    mruSuggestions: ReplyWithAttachmentSuggestion[],
    onAttachmentClicked: (
        suggestion: ReplyWithAttachmentSuggestion,
        suggestionIndex: number
    ) => void,
    shouldUseSuggetedFilesTitle: boolean
): IContextualMenuItem[] {
    if (mruSuggestions.length > 0) {
        let keyIndex: number = 0;
        const attachmentSuggestionMenuItems = mruSuggestions.map(suggestion =>
            createAttachButtonContextualMenuItem(suggestion, keyIndex++, onAttachmentClicked)
        );

        return [createReplyWithContextualMenuItemHeader(shouldUseSuggetedFilesTitle)].concat(
            attachmentSuggestionMenuItems
        );
    } else {
        return [];
    }
}

function createReplyWithContextualMenuItemHeader(
    shouldUseSuggetedFilesTitle: boolean
): IContextualMenuItem {
    return {
        key: 'replyWithHeader',
        name: shouldUseSuggetedFilesTitle ? loc(suggestedFilesText) : loc(replyWithMenuHeaderText),
        itemType: ContextualMenuItemType.Header,
        iconProps: {
            iconName: null,
            styles: {
                root: {
                    margin: '0px',
                    width: '0px',
                },
            },
        },
        className: styles.replyWithHeaderForBottomBar,
    };
}

function createAttachButtonContextualMenuItem(
    suggestion: ReplyWithAttachmentSuggestion,
    keyIndex: number,
    onAttachmentClicked: (
        suggestion: ReplyWithAttachmentSuggestion,
        suggestionIndex: number
    ) => void
): IContextualMenuItem {
    return {
        key: 'suggestion' + keyIndex,
        className: styles.attachmentSuggestionButtonRoot,
        onRender: (
            item: any,
            dismissMenu: (ev?: any, dismissAll?: boolean) => void
        ): React.ReactNode => {
            const props = { suggestion, keyIndex, dismissMenu, onAttachmentClicked };
            return <AttachmentSuggestionButton {...props} />;
        },
    };
}

const AttachmentSuggestionButton = observer(function AttachmentSuggestionButton(props: {
    suggestion: ReplyWithAttachmentSuggestion;
    keyIndex: number;
    dismissMenu: (ev?: any, dismissAll?: boolean) => void;
    onAttachmentClicked: (
        suggestion: ReplyWithAttachmentSuggestion,
        suggestionIndex: number
    ) => void;
}): JSX.Element {
    const { suggestion, keyIndex, dismissMenu, onAttachmentClicked } = props;
    const onClickCallback = React.useCallback(() => {
        onAttachmentClicked(suggestion, keyIndex);
        dismissMenu();
        logAttachmentSuggestionClicked(suggestion);
    }, [suggestion]);
    return (
        <button
            className={styles.attachmentSuggestionButton}
            title={getTooltipForAttachmentSuggestion(suggestion)}
            name={suggestion.FileName}
            aria-label={loc(replyWithMenuHeaderText) + '  ' + suggestion.FileName}
            aria-posinset={1}
            aria-setsize={7}
            aria-disabled="false"
            role="menuitem"
            onClick={onClickCallback}
            tabIndex={0}>
            <div className={styles.attachmentSuggestionButtonContent}>
                <div
                    className={styles.attachmentSuggestionButtonImageContainer}
                    role="presentation"
                    aria-hidden="true">
                    {getIconForSuggestedAttachment(suggestion)}
                </div>
                <div>
                    <div className={styles.attachmentSuggestionButtonTextContainer}>
                        <div className={styles.attachmentSuggestionPrimaryText}>
                            {middleTruncateFileName(
                                fixFileName(suggestion) + getFileExtension(suggestion)
                            )}
                        </div>
                    </div>
                    <div className={styles.attachmentSuggestionSecondaryText}>
                        {getSecondaryTextForAttachmentSuggestion(suggestion)}
                    </div>
                </div>
            </div>
        </button>
    );
});

function getIconForSuggestedAttachment(suggestion: ReplyWithAttachmentSuggestion): JSX.Element {
    const nameWithExtension = suggestion.FileName + getFileExtension(suggestion);

    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        // Classic attachment, so use regular icon
        const attachmentIconCSS: string = getIconCSSForAttachment(
            nameWithExtension,
            null /* contentType */,
            false /* isBlocked */,
            false /* isFolder */,
            null /* embeddedItemClass */
        ).medium;

        return <div className={attachmentIconCSS} />;
    } else {
        // Cloudy attachment, so use cloudy icon
        return (
            <CloudIconWithOverlay
                fileName={nameWithExtension}
                contentType={null}
                isBlocked={false}
                isFolder={false}
                embeddedItemClass={null}
                cloudyIconStyle={styles.cloudyIcon}
                fileIconStyle={styles.fileIcon}
            />
        );
    }
}

function getAccessDate(suggestion: ReplyWithAttachmentSuggestion): string {
    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        return null;
    } else {
        const { DateAccessed: dateAccessed } = suggestion;

        if (!dateAccessed) {
            // For files in cloud this can happen if user received a file which has
            // org/anonymous link and user hasn't opened the file. As for ODSP user
            // gets ACLed to file only after they open it in such cases, hence we get
            // suggestions for files missing this data because the service can't access
            // it yet on behalf of user. Once user is ACLed these properties next time
            // should have values.
            logUsage('AttachmentSuggestionNoAccessDate', undefined, {
                cosmosOnlyData: JSON.stringify({ suggestionId: suggestion.Id }),
                isCosmosDatapoint: true,
            });

            return null;
        }

        const date: OwaDate = owaDate('UTC', dateAccessed);
        const dateAccessedText = formatPastRelativeDateTime(date, observableNow());

        return format(loc(accessedOnText), dateAccessedText);
    }
}

function getModifiedDate(suggestion: ReplyWithAttachmentSuggestion): string {
    const { DateModified: dateModified } = suggestion;

    if (!dateModified) {
        // For files in cloud this can happen if user received a file which has
        // org/anonymous link and user hasn't opened the file. As for ODSP user
        // gets ACLed to file only after they open it in such cases, hence we get
        // suggestions for files missing this data because the service can't access
        // it yet on behalf of user. Once user is ACLed these properties next time
        // should have values.
        logUsage('AttachmentSuggestionNoModifiedDate', undefined, {
            cosmosOnlyData: JSON.stringify({ suggestionId: suggestion.Id }),
            isCosmosDatapoint: true,
        });

        return null;
    }

    const date: OwaDate = owaDate('UTC', dateModified);
    const lastModifiedDateText: string = formatUserDateTime(date);

    return format(loc(replyWithAttachmentLastModifiedText), lastModifiedDateText);
}

/**
 * For cloudy attachments, we are parsing a sharepoint or onedrive URL.
 * For classic attachments, we are parsing an email folder path ('\Inbox\Address to me' for example)
 */
function getBreadcrumbedFileOrEmailPath(
    suggestion: ReplyWithAttachmentSuggestion,
    middleTruncate: boolean = false
): string {
    let breadcrumbedFileOrEmailPath: string = null;

    // Protect all logic with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        if (
            suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
            suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
        ) {
            // Just display the name of the email containing the attachment.
            breadcrumbedFileOrEmailPath = suggestion.SourceTitle;
        } else {
            const pathBreadcrumbs = getBreadcrumbsForFile(suggestion);
            breadcrumbedFileOrEmailPath = middleTruncate
                ? createMiddleTruncatedBreadcrumbsString(pathBreadcrumbs)
                : createBreadcrumbString(pathBreadcrumbs);
        }
    } catch (e) {
        trace.trace.info(
            '[breadcrumbedFileOrEmailPath] Failed to parse attachment data, message: ' + e.message
        );
    } finally {
        return breadcrumbedFileOrEmailPath;
    }
}

function createBreadcrumbString(breadcrumbs: string[]) {
    const isRTL: boolean = getRTL();

    if (isRTL) {
        return breadcrumbs.reverse().join(RTL_PATH_SEPARATOR);
    } else {
        return breadcrumbs.join(LTR_PATH_SEPARATOR);
    }
}

function createMiddleTruncatedBreadcrumbsString(breadcrumbs: string[]) {
    const breadcrumbsString = createBreadcrumbString(breadcrumbs);
    return middleTruncateString(breadcrumbsString, MAX_BREADCRUMBS_LENGTH);
}

function getBreadcrumbsForFile(suggestion: ReplyWithAttachmentSuggestion): string[] | null {
    // Protect all logic with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        if (
            suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
            suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
        ) {
            // Just display the name of the email containing the attachment.
            return null;
        } else {
            // To parse URL for cloudy attachment, create temporary anchor tag.
            const tempAnchorTag: HTMLAnchorElement = document.createElement('a');
            tempAnchorTag.href = suggestion.Url;

            // Hostname should be something like sharepoint.microsoft.com
            // Pathname should be something like /documents/file.docx
            // Splitting the pathname using / as the delimiter, we can ignore the first and last tokens,
            // as the file path does not include the name of the file itself.
            const hostname = decodeURIComponent(tempAnchorTag.hostname);

            // We ignore the first and last element of the result. As first element is empty
            // and last element is the name of the file which we don't want part of breadcrumbs
            const [, ...pathBreadcrumbs] = decodeURIComponent(tempAnchorTag.pathname).split('/');
            pathBreadcrumbs.pop();

            return [hostname, ...pathBreadcrumbs];
        }
    } catch (e) {
        trace.trace.info(
            '[breadcrumbedFileOrEmailPath] Failed to parse attachment data, message: ' + e.message
        );
        return [];
    }
}

function getSecondaryTextForAttachmentSuggestion(suggestion: ReplyWithAttachmentSuggestion) {
    if (
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassic ||
        suggestion.Endpoint === ReplyWithEndpointType.MRUClassicContextual
    ) {
        return getBreadcrumbedFileOrEmailPath(suggestion);
    } else {
        return getAccessDate(suggestion);
    }
}

function getTooltipForAttachmentSuggestion(suggestion: ReplyWithAttachmentSuggestion): string {
    let tooltipString: string = null;

    // Protect with try/catch since we're parsing data that may not exist in the format we expect.
    try {
        tooltipString = [
            suggestion.FileName,
            getBreadcrumbedFileOrEmailPath(suggestion),
            getAccessDate(suggestion),
            getModifiedDate(suggestion),
        ]
            .filter(str => str)
            .join('\n');
    } catch (e) {
        trace.trace.info(
            '[getTooltipForAttachmentSuggestion] Failed to parse attachment data, message: ' +
                e.message
        );
    } finally {
        return tooltipString;
    }
}

function fixFileName(suggestion: ReplyWithAttachmentSuggestion): string {
    // If file name contains file extension, remove it. We'll add it separately.
    const fileExtension: string = '.' + suggestion.FileExtension;
    if (suggestion.FileName.indexOf(fileExtension) >= 0) {
        return suggestion.FileName.replace(fileExtension, '');
    } else {
        return suggestion.FileName;
    }
}

function middleTruncateFileName(fileName: string) {
    // Do not truncate the string if it contains 35 characters or fewer.
    return middleTruncateString(fileName, MAX_FILENAME_LENGTH);
}

function middleTruncateString(str: string, maxLength: number) {
    if (str.length <= maxLength) {
        return str;
    } else {
        const charsToShow: number = maxLength - TRUNCATION_SEPARATOR.length;
        const frontChars: number = Math.ceil(charsToShow / 2);
        const backChars: number = Math.floor(charsToShow / 2);
        return (
            str.substr(0, frontChars) + TRUNCATION_SEPARATOR + str.substr(str.length - backChars)
        );
    }
}

function getFileExtension(suggestion: ReplyWithAttachmentSuggestion) {
    return '.' + suggestion.FileExtension;
}
